import { getApiHost } from '@hotjar/common';
import { hjGetCookie, hjSetCookie } from '@hotjar/webapp/utils/cookies';

angular.module('hjSurvey', ['restangular', 'ngSentry', 'hjApp', 'hjApp.api']);

angular.module('hjSurvey').controller('ShowCtrl', [
    '$scope',
    '$window',
    'Restangular',
    'restDefaultHandlers',
    function ($scope, $window, Restangular, restDefaultHandlers) {
        /*************************
         * PRIVATE VARIABLES
         *************************/
        Restangular.setBaseUrl(getApiHost() + '/api/v1');

        var siteId = getParameterByName('siteId'),
            surveyId = getParameterByName('surveyId'),
            preview = getParameterByName('preview') === '1',
            rSurvey = Restangular.all('client').one('sites', siteId).one('surveys', surveyId),
            activeLanguage = 'en';

        /*************************
         * SCOPE VARIABLES
         *************************/
        $scope.model = {
            completed: hasCompletedSurvey(),
            loaded: false,
            hasRequiredFields: false,
            netScores: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10], // Possible scores in net promoter score.
            rating5: [1, 2, 3, 4, 5], // Possible scores in likert 1 - 5.
            rating7: [1, 2, 3, 4, 5, 6, 7], // Possible scores in likert 1 - 7.
            rtlLanguages: ['ar', 'fa', 'he'],
            isRtlLanguage: false,
            survey: null,
            responses: {},
            sending: false
        };

        /*************************
         * INITIALIZATION
         *************************/
        init();

        /**
         * Initialize survey.
         */
        function init() {
            // If this is a preview, just add an EventListener which will include the survey data.
            if (preview) {
                window.addEventListener('message', onMessage, false);
                return;
            }

            // We can't load a survey without the IDs.
            if (!(siteId && surveyId)) {
                surveyUnavailable();
                return;
            }

            // Get the content of the survey.
            rSurvey.get().then(
                function (res) {
                    if (res.success) {
                        if (res.redirect) {
                            $window.location.href = res.redirect;
                        } else {
                            showSurvey(res.survey);
                        }
                    } else {
                        // If success is false, show the unavailable page.
                        surveyUnavailable();
                    }
                },
                function () {
                    // On error response (such as 401, 403), show the unavailable page.
                    surveyUnavailable();
                }
            );
        }

        /**
         * Message handler required so that the parent displaying this in an iFrame can
         * inject the survey to be previewed.
         *
         * @param event data attribute - survey to preview.
         */
        function onMessage(event) {
            if (_.get(event, 'data.type') !== 'survey') {
                return;
            }

            if (!_.endsWith(event.origin, 'hotjar.com')) {
                return;
            }

            var survey = event.data.data;

            $scope.$apply(function () {
                showSurvey(survey);

                // When previewing check what status the 'show_branding' option should be.
                $scope.model.survey.effective_show_branding = $scope.model.survey.show_branding;
            });
        }

        /*************************
         * WINDOW FUNCTIONS
         *************************/
        if (preview) {
            window.addEventListener('message', onMessage, false);
        }

        /*************************
         * PRIVATE FUNCTIONS
         *************************/
        /**
         * Show a survey on the page.
         *
         * @param survey {object} Survey object.
         */
        function showSurvey(survey) {
            $scope.model.survey = survey;

            // If we don't do some fallback values here the page will fail to load at all.
            $scope.model.survey.content.description = $scope.model.survey.content.description || '';
            $scope.model.survey.content.thankyou = $scope.model.survey.content.thankyou || '';

            // Replace new lines with <br/> in the description and thank-you messages
            $scope.model.survey.content.description = $scope.model.survey.content.description.replace(/\n/g, '<br/>');
            $scope.model.survey.content.thankyou = $scope.model.survey.content.thankyou.replace(/\n/g, '<br/>');

            _.each($scope.model.survey.content.questions, function (question) {
                // Since we use uuids to group answers, if we have an old survey they doesn't have uuids, then use the
                // question text. This is the same as the old behavior.
                question.uuid = question.uuid || question.text;

                // Shuffle the answers for questions that should show in a random order.
                if (question.randomize_answer_order) {
                    question.answers = _.shuffle(question.answers);
                }
            });

            // Load the language strings.
            setLanguage($scope.model.survey.language);

            // Create the responses obj which will hold in it all the responses data.
            $scope.createResponsesObj();

            $scope.model.loaded = true;
        }

        /**
         * Get the response data to post to the server on submit.
         */
        function getResponseData() {
            var responseData = { answers: [], version: 4 },
                answers;

            _.each($scope.model.survey.content.questions, function (question) {
                answers = $scope.model.responses[question.uuid];
                if (answers.length > 0) {
                    // Loop through all the different answers.
                    _.each(answers, function (answer) {
                        responseData.answers.push({
                            answer: answer.answer,
                            comment: answer.comment,
                            question: question.text,
                            questionUuid: question.uuid
                        });
                    });
                } else {
                    // No answer available for this question, send in an 'empty' answer.
                    responseData.answers.push({
                        answer: null,
                        comment: null,
                        question: question.text,
                        questionUuid: question.uuid
                    });
                }
            });

            return responseData;
        }

        /**
         * Post the survey response to the server.
         */
        function postResponse() {
            if (preview) {
                // If this is a preview, post nothing and set the survey to completed.
                showThankyouPage();
            } else {
                rSurvey
                    .customPOST({
                        action: 'survey_response',
                        responses: getResponseData()
                    })
                    .then(function (res) {
                        if (res.success) {
                            showThankyouPage();
                            setSurveyCompleted();
                        } else {
                            alert(
                                'An error occurred while submitting the survey. ' +
                                    'This is probably due to the survey not being active.'
                            );
                        }
                    }, restDefaultHandlers.onError);
            }
        }

        /**
         * Take the user to the thank you page on completion of the survey.
         */
        function showThankyouPage() {
            $scope.model.completed = true;

            // Important: Even though scrolling to the top is not really necessary in most cases
            // since the page will shrink in size, browser extensions such as grammarly cause
            // an issue which retains the page's height which would result in a "white page"
            // on submission. Leave this function here.
            scrollToTop();
        }

        /**
         * Return true if this visitor has already completed the survey.
         */
        function hasCompletedSurvey() {
            return getCompletedSurveys().indexOf(String(surveyId)) !== -1;
        }

        /**
         * Remember that this visitor has answered this survey.
         */
        function setSurveyCompleted() {
            var completedSurveys = getCompletedSurveys();

            completedSurveys.push(surveyId);
            hjSetCookie('_hjDoneSurveys', completedSurveys.join(','));
        }

        /**
         * Get an array containing all the previously completed surveys.
         */
        function getCompletedSurveys() {
            var doneSurveys = hjGetCookie('_hjDoneSurveys');

            return doneSurveys ? doneSurveys.split(',') : [];
        }

        /**
         * Show the survey unavailable screen.
         */
        function surveyUnavailable() {
            setLanguage('en');
            $scope.model.survey = false;
            $scope.model.loaded = true;
        }

        /**
         * Get a parameter from the querystring by name.
         *
         * @param name {string} Name of parameter to get.
         * @returns {string} Value of parameter or empty string if it wasn't found.
         */
        function getParameterByName(name) {
            var regex, results;

            name = name.replace(/\[/, '\\[').replace(/]/, '\\]');
            regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
            results = regex.exec(location.search);

            return results == null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
        }

        /**
         * Scroll to the top of the page.
         */
        function scrollToTop() {
            window.scrollTo(0, 0);
        }

        /**
         * Set the active language. Must be called before starting to fetch translated strings.
         *
         * @param language {string} Language's ISO code to set, e.g. 'en'.
         */
        function setLanguage(language) {
            // Fix for incorrect ISO code for Chinese simplified.
            // Retaining this condition due to cache.
            language = language === 'zh' ? 'zh_CN' : language;

            // Double quotes allowed as an exception, due to likely inclusion of single quote in strings.
            /* eslint-disable quotes */
            var surveyStrings = {
                af: {
                    send: 'Stuur',
                    survey_not_available: 'Opname nie beskikbaar nie.',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Die opname wat jy probeer bereik het bestaan nie meer nie of het nog nie bestaan nie.',
                    'this_survey_has_no_questions_yet.': 'Die opname het nog geen antwoorde nie.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Proef asb. jou antwoorde vir die plig velde wat in rooi gemerk is.',
                    please_type_here: 'Tik asb. hier ...',
                    'questions_marked_with_an_*_are_required.': "Vrae gemerk met 'n * is verpligte vrae."
                },
                ar: {
                    send: 'أرسِل',
                    survey_not_available: 'الاستطلاع غير متوفر',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'الاستطلاع الذي حاولت الدخول اليه غير موجود أو انتهت مدته.',
                    'this_survey_has_no_questions_yet.': 'هذا الاستطلاع لا يحتوي على أسئلة.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'فضلاً، قم بفحص أجوبتك في الخانات المعلّمة بالأحمر.',
                    please_type_here: 'الرجاء الكتابة هنا...',
                    'questions_marked_with_an_*_are_required.': 'النجمة * تعني أن الإجابة على هذه الأسئلة ضرورية.'
                },
                bg: {
                    send: 'Изпрати',
                    survey_not_available: 'Анкетата не е достъпна',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Анкетата, която се опитвате да видите или вече не е актуална, или не съществува.',
                    'this_survey_has_no_questions_yet.': 'В тази анкета все още няма въпроси.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Моля проверете отговорите си за задължителните полета маркирани с червено.',
                    please_type_here: 'Моля напишете Вашия отговор тук...',
                    'questions_marked_with_an_*_are_required.': 'Въпросите, маркирани със *, са задължителни.'
                },
                ca: {
                    send: 'Envia',
                    survey_not_available: 'Enquesta no disponible.',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'La enquesta que intentes accedir ha expirat o ja no existeix.',
                    'this_survey_has_no_questions_yet.': 'Aquesta enquesta encara no té preguntes.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Si us plau, comprova les preguntes que estan marcades en vermell.',
                    please_type_here: 'Introdueix aquí...',
                    'questions_marked_with_an_*_are_required.': 'Les preguntes marcades amb * són obligatòries.'
                },
                cs: {
                    send: 'Odeslat',
                    survey_not_available: 'Průzkum není dostupný',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Průzkum, ke kterému se snažíte přistoupit, již vypršel nebo neexistuje.',
                    'this_survey_has_no_questions_yet.': 'Tento průzkum zatím nemá žádné otázky.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Prosím, zkontrolujte své odpovědi v červeně označených políčkách.',
                    please_type_here: ' Zde prosím odpovězte...',
                    'questions_marked_with_an_*_are_required.': 'Otázky označené * jsou povinné.'
                },
                cy: {
                    send: 'Anfon',
                    survey_not_available: "Nid yw'r arolwg ar gael.",
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        "Mae'r arolwg y gwnaethoch geisio ei agor un ai wedi dod i ben neu nid yw'n bodoli",
                    'this_survey_has_no_questions_yet.': 'Nid oes unrhyw gwestiynnau yn yr arolwg yma ar hyn o bryd',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        "Gwiriwch eich atebion ar gyfer y meysydd angenrheidiol sydd wedi'u marcio yn goch",
                    please_type_here: 'Teipiwch yma os gwelwch yn dda...',
                    'questions_marked_with_an_*_are_required.': 'Mae cwestiynau sydd wedi eu nodi â * yn angenrheidiol.'
                },
                da: {
                    send: 'Send',
                    survey_not_available: 'Spørgeskemaet er desværre ikke tilgængeligt',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Spørgeskemaet du forsøgte at tilgå, er enten udløbet eller eksisterer ikke',
                    'this_survey_has_no_questions_yet.': 'Dette spørgeskema har ingen spørgsmål endnu.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Se venligst dine svar i de obligatoriske felter markeret med rødt.',
                    please_type_here: 'Skriv venligst her...',
                    'questions_marked_with_an_*_are_required.': 'Spørgsmål markeret med * er obligatoriske.'
                },
                de: {
                    send: 'Senden',
                    survey_not_available: 'Umfrage ist nicht verfügbar',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Die Umfrage, die Sie aufrufen möchten, ist abgelaufen oder existiert nicht.',
                    'this_survey_has_no_questions_yet.': 'Diese Umfrage hat noch keine Fragen.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Bitte überprüfen Sie Ihre Antworten in den rot markierten Feldern.',
                    please_type_here: 'Bitte hier schreiben...',
                    'questions_marked_with_an_*_are_required.': 'Fragen mit einem * sind Pflichtfelder.'
                },
                el: {
                    send: 'Αποστολή',
                    survey_not_available: 'Έρευνα μη διαθέσιμη',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Η έρευνα που προσπαθήσατε να βρείτε έχει λήξει ή δεν υπάρχει.',
                    'this_survey_has_no_questions_yet.': 'Η έρευνα δεν έχει ερωτήσεις ακόμα.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Παρακαλώ ελέγξτε τις απαντήσεις σας στα απαραίτητα πεδία.',
                    please_type_here: 'Παρακαλώ πληκτρολογήστε εδώ...',
                    'questions_marked_with_an_*_are_required.': 'Οι ερωτήσεις με * είναι απαραίτητες.'
                },
                en: {
                    send: 'Send',
                    survey_not_available: 'Survey not available',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'The survey you tried to access has either expired or does not exist.',
                    'this_survey_has_no_questions_yet.': 'This survey has no questions yet.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Please check your answers for the required fields marked in red.',
                    please_type_here: 'Please type here...',
                    'questions_marked_with_an_*_are_required.': 'Questions marked with an * are required.'
                },
                es: {
                    send: 'Enviar',
                    survey_not_available: 'Encuesta no disponible',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'La encuesta a la que intenta acceder ha expirado o ya no existe.',
                    'this_survey_has_no_questions_yet.': 'Esta encuesta aún no tiene preguntas.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Por favor, revise sus respuestas, los campos marcados en rojo son obligatorios.',
                    please_type_here: 'Por favor, escribe aquí...',
                    'questions_marked_with_an_*_are_required.': 'Las preguntas marcadas con un * son obligatorias.'
                },
                et: {
                    send: 'Saada',
                    survey_not_available: 'Küsitlust ei leitud',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Küsitlus on aegunud või enam ei eksisteeri.',
                    'this_survey_has_no_questions_yet.': 'Küsitlusele ei ole veel lisatud küsimusi.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Palun kontrollige punasega märgitud kohustuslikke väljasid!',
                    please_type_here: 'Palun sisestage siia...',
                    'questions_marked_with_an_*_are_required.': 'Tärniga (*) märgitud küsimused on kohustuslikud.'
                },
                fa: {
                    send: 'بفرست',
                    survey_not_available: 'نظرسنجی در دسترس نیست',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'نظرسنجی که درخواست دادین یا منقضی شده یا وجود ندارد',
                    'this_survey_has_no_questions_yet.': 'این نظرسنجی هنوز سوالی ندارد',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'لطفا جواب‌های خود را چک کنید و همه آنهایی که قرمز شدند را پاسخ دهید',
                    please_type_here: 'لطفا اینجا بنویسید',
                    'questions_marked_with_an_*_are_required.': 'سوال‌های مشخص شده با * الزامیست'
                },
                fi: {
                    send: 'Lähetä',
                    survey_not_available: 'Kysely ei ole juuri nyt saatavilla',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Kysely johon yritit päästä on joko sulkeutunut tai ei ole olemassa.',
                    'this_survey_has_no_questions_yet.': 'Kyselyssä ei ole vielä kysymyksiä.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Tarkista punaisella merkityt vastaukset.',
                    please_type_here: 'Kirjoita tähän',
                    'questions_marked_with_an_*_are_required.': '* merkityt kysymykset ovat pakollisia.'
                },
                fr: {
                    send: 'Envoyer',
                    survey_not_available: 'Enquête non disponible',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        "L'enquête à laquelle vous essayez d'accéder a expiré ou n'existe pas.",
                    'this_survey_has_no_questions_yet.': "Cette enquête n'a pas encore de questions",
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Vérifiez vos réponses pour les champs entourés de rouge svp',
                    please_type_here: 'Ecrivez ici',
                    'questions_marked_with_an_*_are_required.': "Les questions marquées d'un * sont obligatoires"
                },
                he: {
                    send: 'שלח',
                    survey_not_available: 'הסקר לא זמין כעת',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'הסקר אליו ניסית להגיע לא קיים או שפג תוקפו',
                    'this_survey_has_no_questions_yet.': 'עדיין אין שאלות בסקר זה',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'בדוק שוב את תשובותיך לשדות המסומנית באדום',
                    please_type_here: 'הקלד כאן...',
                    'questions_marked_with_an_*_are_required.': 'השאלות המסומנות עם * הינן חובה'
                },
                hr: {
                    send: 'Pošalji',
                    survey_not_available: 'Anketa nije dostupna',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Anketa koju tražite je istekla ili ne postoji.',
                    'this_survey_has_no_questions_yet.': 'Ova anketa još nema pitanja.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Molimo provjerite jeste li odgovorili na sva polja označena crveno.',
                    please_type_here: 'Pišite ovdje',
                    'questions_marked_with_an_*_are_required.': 'Pitanja označena sa * su obavezna.'
                },
                hu: {
                    send: 'Küldés',
                    survey_not_available: 'A kérdőív nem elérhető',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Ez a kérdőív már lejárt, vagy törölték.',
                    'this_survey_has_no_questions_yet.': 'A kérdőív még nem tartalmaz kérdéseket.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Kérjük, ellenőrizd a pirossal megjelölt válaszaidat.',
                    please_type_here: 'Ide írhat...',
                    'questions_marked_with_an_*_are_required.': 'A *-gal megjelölt kérdések kötelezőek.'
                },
                id: {
                    send: 'Kirim',
                    survey_not_available: 'Survei tidak tersedia',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Survei yang Anda coba akses sudah berakhir atau tidak ada.',
                    'this_survey_has_no_questions_yet.': 'Survei ini belum memiliki pertanyaan.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Silahkan periksa kembali untuk jawaban Anda yang ditandai dengan warna merah.',
                    please_type_here: 'Silahkan ketik disini...',
                    'questions_marked_with_an_*_are_required.': 'Pertanyaan dengan tanda * adalah wajib.'
                },
                it: {
                    send: 'Invia',
                    survey_not_available: 'Questionario non disponibile',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Il questionario a cui stai provando ad accedere potrebbe essere scaduto o non esistere.',
                    'this_survey_has_no_questions_yet.': 'Il questionario non ha ancora domande.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Per favore controlla le risposte per i campi obbligatori contrassegnati in rosso.',
                    please_type_here: 'Scrivi qui...',
                    'questions_marked_with_an_*_are_required.': 'Le domande contrassegnate con un * sono obbligatorie.'
                },
                ja: {
                    send: '送信',
                    survey_not_available: '調査利用不可',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'お客様が該当した調査がありません、又は期限切れになりました。',
                    'this_survey_has_no_questions_yet.': '本調査の質問がありません。',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        '赤色で強調している答に確認してください。',
                    please_type_here: 'こちらにご記入ください...',
                    'questions_marked_with_an_*_are_required.': '「*」がある質問は必ずご入力してください。'
                },
                ko: {
                    send: '보내기',
                    survey_not_available: '설문 내용이 없습니다.',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        '이 설문은 더이상 존재하지 않습니다.',
                    'this_survey_has_no_questions_yet.': '이 설문에는 아직 질문이 없습니다.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        '빨간색으로 표시된 필수 항목에 답변을 다시 확인해주세요.',
                    please_type_here: '여기에 입력해주세요',
                    'questions_marked_with_an_*_are_required.': '* 표시는 필수 답변 항목입니다'
                },
                lt: {
                    send: 'Siųsti',
                    survey_not_available: 'Apklausa negalima',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Apklausa, kurią bandote pasiekti, pasibaigė arba neegzistuoja.',
                    'this_survey_has_no_questions_yet.': 'Apklausa kolkas neturi klausimų.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Prašome patikrinti raudonai pažymėtus laukus.',
                    please_type_here: 'Rašyti čia...',
                    'questions_marked_with_an_*_are_required.': 'Klausimai, pažymėti *, yra privalomi.'
                },
                lv: {
                    send: 'Nosūtīt',
                    survey_not_available: 'Aptauja nav pieejama',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Aptauja, kurai Jūs mēģināt pieklūt, ir beigusies vai neeksistē.',
                    'this_survey_has_no_questions_yet.': 'Šī aptaujā nav jautājumu.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Lūdzu, pārbaudiet Jūsu atbildes uz jautājumiem, kuri atzīmēti ar sarkano.',
                    please_type_here: 'Lūdzu, ievadiet šeit...',
                    'questions_marked_with_an_*_are_required.': 'Jautājumi, atzīmēti ar * ir obligāti.'
                },
                mis: {
                    send: 'Pošalji',
                    survey_not_available: 'Anketa nije dostupna',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Anketa koju tražite je istekla ili ne postoji.',
                    'this_survey_has_no_questions_yet.': 'Ova anketa još uvijek nema pitanja.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Molimo Vas da provjerite jeste li odgovorili na sva polja označena crvenom.',
                    please_type_here: 'Pišite ovdje...',
                    'questions_marked_with_an_*_are_required.': 'Pitanja označena sa * su obavezna.'
                },
                nb: {
                    send: 'Send',
                    survey_not_available: 'Spørreskjemaet er ikke tilgjengelig',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Spørreskjemaet er enten utløpt eller finnes ikke.',
                    'this_survey_has_no_questions_yet.': 'Spørreskjemaet har ikke noen spørsmål ennå.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Vennlist se på svarene i de obligatoriske feltene merket i rødt.',
                    please_type_here: 'Skriv her...',
                    'questions_marked_with_an_*_are_required.': 'Spørsmål markert med * er obligatoriske.'
                },
                nl: {
                    send: 'Verstuur',
                    survey_not_available: 'Enquête niet beschikbaar',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Deze enquête is afgesloten of bestaat niet.',
                    'this_survey_has_no_questions_yet.': 'Deze enquête bevat nog geen vragen.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Controleer alstublieft uw antwoorden in de verplichte velden die rood zijn gemarkeerd.',
                    please_type_here: 'Jouw bericht...',
                    'questions_marked_with_an_*_are_required.': 'Vragen met een * zijn verplicht.'
                },
                pl: {
                    send: 'Wyślij',
                    survey_not_available: 'Ankieta niedostępna',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Ankieta, którą próbujesz otworzyć nie istnieje lub wygasła.',
                    'this_survey_has_no_questions_yet.': 'Ta ankieta nie ma jeszcze zdefiniowanych żadnych pytań.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Sprawdź odpowiedzi na wymagane pytania (zaznaczone na czerwono).',
                    please_type_here: 'Wpisz wiadomość...',
                    'questions_marked_with_an_*_are_required.': 'Pytania oznaczone * są wymagane.'
                },
                pt: {
                    send: 'Enviar',
                    survey_not_available: 'Questionário indisponível',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'O questionário que tentou aceder expirou ou não existe.',
                    'this_survey_has_no_questions_yet.': 'Este questionário ainda não tem questões.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Por favor, verifique as respostas assinaladas a vermelho.',
                    please_type_here: 'Por favor, escreva aqui ...',
                    'questions_marked_with_an_*_are_required.':
                        'Campos marcados com * são de preenchimento obrigatório.'
                },
                pt_BR: {
                    send: 'Enviar',
                    survey_not_available: 'Pesquisa não disponível',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'A pesquisa feita expirou ou não está acessível.',
                    'this_survey_has_no_questions_yet.': 'Esta pesquisa ainda não possui perguntas.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Por favor, verifique suas respostas para os campos obrigatórios marcados em vermelho.',
                    please_type_here: 'Por favor, escreva aqui...',
                    'questions_marked_with_an_*_are_required.': '*Perguntas Obrigatórias.'
                },
                ro: {
                    send: 'Trimite',
                    survey_not_available: 'Chestionar indisponibil',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Chestionarul pe care incerci să îl accesezi a expirat sau nu există.',
                    'this_survey_has_no_questions_yet.': 'Acest chestionar nu are încă întrebări.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Te rugăm verifică răspunsurile pentru căsuțele marcate cu roșu.',
                    please_type_here: 'Scrie aici...',
                    'questions_marked_with_an_*_are_required.': 'Întrebarile marcate cu * sunt obligatorii.'
                },
                ru: {
                    send: 'Отправить',
                    survey_not_available: 'Опрос недоступен',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Данный опрос устарел, либо не существует.',
                    'this_survey_has_no_questions_yet.': 'Данный опрос пока не содержит вопросов.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Проверьте, пожалуйста, ответы в полях, выделенных красным цветом.',
                    please_type_here: 'Место для ввода...',
                    'questions_marked_with_an_*_are_required.': 'Вопросы помеченные * являются обязательными.'
                },
                sk: {
                    send: 'Odoslať',
                    survey_not_available: 'Anketa je nedostupná',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Anketa už viac nie je platná alebo neexistuje.',
                    'this_survey_has_no_questions_yet.': 'Anketa nemá žiadne otázky.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Prosím, skontrolujte odpovede označené červenou.',
                    please_type_here: 'Začnite písať tu',
                    'questions_marked_with_an_*_are_required.': 'Otázky označené s * sú povinné'
                },
                sl: {
                    send: 'Pošlji',
                    survey_not_available: 'Anketa ni dostopna',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Poskušate dostopati do ankete, ki je potekla ali pa ne obstaja.',
                    'this_survey_has_no_questions_yet.': 'Anketa še nima vprašanj.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Prosimo preverite obvezna polja za odgovore označena z rdečo barvo.',
                    please_type_here: 'Prosimo vnesite tukaj...',
                    'questions_marked_with_an_*_are_required.': 'Vprašanja označena z * so obvezna.'
                },
                sq: {
                    send: 'Dërgo',
                    survey_not_available: 'Anketimi nuk është i disponueshëm.',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Anketimi që doni te hapni ka skaduar ose nuk eksizton.',
                    'this_survey_has_no_questions_yet.': 'Nuk ka ende pyetje për këtë anketim.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Ju lutem kontrolloni që keni dhënë përgjigje për pyetjet e kërkuara të shënuara me të kuqe.',
                    please_type_here: 'Ju lutem shkruani këtu...',
                    'questions_marked_with_an_*_are_required.': 'Pyetjet e shënuara me * janë të kërkuara.'
                },
                sr: {
                    send: 'Pošalji',
                    survey_not_available: 'Anketa nije dostupna',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Anketa koju tražite je istekla ili ne postoji.',
                    'this_survey_has_no_questions_yet.': 'Ova anketa još uvek nema pitanja.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Molimo proverite da li ste odgovorili na sva polja označena crvenom bojom.',
                    please_type_here: 'Pišite ovde...',
                    'questions_marked_with_an_*_are_required.': 'Pitanja označena sa * su obavezna.'
                },
                sv: {
                    send: 'Skicka',
                    survey_not_available: 'Frågeformuläret är inte tillgängligt',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Undersökningen du forsökt ta del av har antingen avslutats eller existerar inte.',
                    'this_survey_has_no_questions_yet.': 'Undersökningen har inga frågor än.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Var vänlig att kolla dom frågor som är obligatoriska som är markerade i rött.',
                    please_type_here: 'Skriv här...',
                    'questions_marked_with_an_*_are_required.': 'Frågor märkta med * är obligatoriska.'
                },
                sw: {
                    send: 'Tuma',
                    survey_not_available: 'Dodoso halipatikani',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Dodoso unalotafuta aidha limepitwa na wakat au halipo.',
                    'this_survey_has_no_questions_yet.': 'Dodoso halina maswali bado.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Tafadhali angalia majibu yako katika sehemu zenye alama nyekundu.',
                    please_type_here: 'Tafadhali andika hapa...',
                    'questions_marked_with_an_*_are_required.': 'Maswali yenye alama * yanahitajika.'
                },
                th: {
                    send: 'ส่ง',
                    survey_not_available: 'ไม่มีแบบสำรวจ',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'แบบสำรวจที่คุณพยายามเข้าถึงอาจไม่มีอยู่จริงหรือหมดอายุไปแล้ว',
                    'this_survey_has_no_questions_yet.': 'แบบสำรวจนี้ยังไม่มีคำถาม',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'โปรดตรวจสอบคำตอบของท่านสำหรับช่องที่จำเป็นในสีแดง',
                    please_type_here: 'โปรดพิมพ์ที่นี่...',
                    'questions_marked_with_an_*_are_required.': 'คำถามที่จำเป็นจะมีเครื่องหมาย *'
                },
                tl: {
                    send: 'Ipadala',
                    survey_not_available: 'Ang survey ay hindi magamit sa oras na ito',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Ang survey na inyong pinuntahan ay maaaring tapos na o hindi pa nagagawa.',
                    'this_survey_has_no_questions_yet.': 'Ang survey na ito ay wala pang mga tanong.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Maari pong paki-tignan ang inyong mga sagot sa mga tanong na may markang pula.',
                    please_type_here: 'Dito po magsimulang magsulat...',
                    'questions_marked_with_an_*_are_required.': 'Ang mga tanong na may markang * ay kailangan.'
                },
                tr: {
                    send: 'Gönder',
                    survey_not_available: 'Ankete erişilemiyor',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Erişmeye çalıştığınız anketin süresi bitmiş veya böyle bir anket bulunmamakta.',
                    'this_survey_has_no_questions_yet.': 'Bu ankete henüz hiç soru eklenmemiş.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Kırmızıyla işaretlenen mecburi alanlara verdiğiniz cevapları kontrol ediniz.',
                    please_type_here: 'Cevabınızı buraya giriniz...',
                    'questions_marked_with_an_*_are_required.': '* ile işaretlenmiş alanları doldurmak mecburidir.'
                },
                uk: {
                    send: 'Надіслати',
                    survey_not_available: 'Опитування недоступно',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Дане опитування або закінчилось, або не існує.',
                    'this_survey_has_no_questions_yet.': 'Дане опитування ще не містить запитань.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Будь ласка, перевірте відповіді на запитання в полях, позначених червоним кольором.',
                    please_type_here: 'Місце для вводу...',
                    'questions_marked_with_an_*_are_required.': "Питання позначені * є обов'язковими."
                },
                vi: {
                    send: 'Gởi',
                    survey_not_available: 'Bản khảo sát không tồn tại',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        'Bản khảo sát bạn muốn truy cập đã hết hạn sử dụng hoặc không tồn tại.',
                    'this_survey_has_no_questions_yet.': 'Bản khảo sát này chưa có câu hỏi nào.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        'Vui lòng kiểm tra câu trả lời cho những trường bắt buộc được tô đỏ.',
                    please_type_here: 'Vui lòng nhập nội dung tại đây...',
                    'questions_marked_with_an_*_are_required.': 'Câu hỏi đánh dấu * là bắt buộc.'
                },
                zh_CN: {
                    send: '发送',
                    survey_not_available: '问卷暂未出炉',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        '您正尝试读取的问卷已过期或并不存在。',
                    'this_survey_has_no_questions_yet.': '这份问卷暂未设置问题。',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        '请检查标有红色印记的答案是否正确。',
                    please_type_here: '请输入...',
                    'questions_marked_with_an_*_are_required.': '带有*的为必填项'
                },
                zh_TW: {
                    send: '送出',
                    survey_not_available: '問券未開放',
                    'the_survey_you_tried_to_access_has_either_expired_or_does_not_exist.':
                        '您正在嘗試存取的問券可能已過期或是並不存在.',
                    'this_survey_has_no_questions_yet.': '這份問券還沒有新增問題.',
                    'please_check_your_answers_for_the_required_fields_marked_in_red.':
                        '請檢查被標記紅色的問題欄位, 答案是否符合要求?',
                    please_type_here: '請輸入...',
                    'questions_marked_with_an_*_are_required.': '*為必填'
                }
            };
            /* eslint-enable quotes */

            if (!(language in surveyStrings)) {
                throw new Error('Invalid language "' + language + '"');
            }

            // Set the strings to be used.
            activeLanguage = surveyStrings[language];

            // Arabic and Hebrew need to read right-to-left not left-to-right.
            $scope.model.isRtlLanguage = $scope.model.rtlLanguages.indexOf(language) > -1;
        }

        /*************************
         * SCOPE FUNCTIONS
         *************************/
        /**
         * Create the responses obj which will hold in it all the response data.
         */
        $scope.createResponsesObj = function () {
            // Reset response-related fields (to avoid previews retaining values).
            _.each($scope.model.survey.content.questions, function (question) {
                question.answerField = undefined;
                question.checked = undefined;
                $scope.model.responses[question.uuid] = [];
                _.each(question.answers || [], function (answer) {
                    answer.checked = undefined;
                    answer.commentsField = undefined;
                });
            });

            // Check if there is at least one required question.
            $scope.model.hasRequiredFields = _.find($scope.model.survey.content.questions, function (question) {
                return question.required === true;
            });
        };

        /**
         * Set the answer for a question (Used by all questions except multiple-close-ended).
         *
         * @param question {string} The question.
         * @param answer {string} The answer.
         */
        $scope.setQuestionAnswer = function (questionUuid, answer) {
            if ($scope.model.responses[questionUuid].length === 0) {
                $scope.model.responses[questionUuid].push({ answer: answer, comment: null });
            } else {
                $scope.model.responses[questionUuid][0] = {
                    answer: answer === undefined || answer === '' ? null : answer,
                    comment: null
                };
            }
        };

        /**
         * Update the answers for a question (Used by multiple-close-ended questions).
         *
         * @param question {string} The question.
         * @param answer {string} The answer.
         */
        $scope.updateQuestionAnswer = function (questionUuid, answer) {
            var questionResponse = angular.copy($scope.model.responses[questionUuid]),
                answerObjIndex = _.findIndex(questionResponse, { answer: answer });

            if (answerObjIndex === -1) {
                questionResponse.push({ answer: answer, comment: null });
            } else {
                questionResponse = _.reject(questionResponse, function (item) {
                    return item.answer === answer;
                });
            }
            $scope.model.responses[questionUuid] = questionResponse;
        };

        /**
         * Set the comment for a question's answer (Used by single-close-ended questions).
         *
         * @param question {string} The question.
         * @param comment {string} The comment.
         */
        $scope.setAnswerComment = function (questionUuid, comment) {
            $scope.model.responses[questionUuid][0].comment = comment === undefined ? null : comment;
        };

        /**
         * Update the comment for a question's answer (Used by multiple-close-ended questions).
         *
         * @param question {string} The question.
         * @param answer {string} The answer.
         * @param comment {string} The comment.
         */
        $scope.updateAnswerComment = function (questionUuid, answer, comment) {
            var questionResponse = $scope.model.responses[questionUuid],
                answerObjIndex = _.findIndex(questionResponse, { answer: answer });

            $scope.model.responses[questionUuid][answerObjIndex].comment = comment === undefined ? null : comment;
        };

        /**
         * Submit the survey form.
         */
        $scope.submit = function () {
            $scope.model.sending = true;

            if ($scope.submitted && $scope.surveyForm.$valid) {
                // Post response if form is valid.
                postResponse();
            } else {
                // Scroll to top if the form isn't valid so the user sees the error message.
                $scope.model.sending = false;
                scrollToTop();
            }
        };

        /**
         * Localization service (print).
         *
         * @param str {string} String to localize.
         */
        $scope._ = function (str) {
            if (!activeLanguage) {
                throw new Error('No active language has been set yet.');
            }

            return activeLanguage[str];
        };
    }
]);
